import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";

interface Message {
  id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  ts: number;
  tool?: string;
  toolInput?: unknown;
}

interface ChatStatus {
  ok: boolean;
  reason?: string;
  auth_method?: string;
}

function getApiBase(): string {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return base;
}

function getToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("coagent_console_token");
}

export function Chat() {
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const scrollerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const sshCommand = `ssh -t gitai 'HOME=/opt/coagent-console-prep/claude-auth HTTPS_PROXY=http://89.124.92.161:8443 HTTP_PROXY=http://89.124.92.161:8443 claude setup-token'`;

  const refreshStatus = () =>
    api.chatStatus().then(setStatus).catch(() => setStatus({ ok: false, reason: "unreachable" }));

  useEffect(() => {
    refreshStatus();
  }, []);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [messages]);

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopyStatus("copied");
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        setCopyStatus(ok ? "copied" : "failed");
      }
    } catch {
      setCopyStatus("failed");
    }
    setTimeout(() => setCopyStatus("idle"), 2500);
  };

  const parseSSE = (
    chunk: string,
    onEvent: (type: string, data: Record<string, unknown>) => void,
    state: { buf: string },
  ) => {
    state.buf += chunk;
    // Split blocks on blank line (handle both \n\n and \r\n\r\n)
    const blocks = state.buf.split(/\r?\n\r?\n/);
    state.buf = blocks.pop() ?? "";
    for (const block of blocks) {
      const lines = block.split(/\r?\n/);
      let eventType = "message";
      let dataStr = "";
      for (const ln of lines) {
        const line = ln.trimEnd();
        if (!line) continue;
        if (line.startsWith("event:")) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          const piece = line.slice(5).replace(/^\s/, "");
          dataStr += (dataStr ? "\n" : "") + piece;
        }
      }
      if (!dataStr) continue;
      try {
        const data = JSON.parse(dataStr);
        onEvent(eventType, data);
      } catch (err) {
        console.warn("[sse] parse failed", err, "raw:", dataStr.slice(0, 200));
      }
    }
  };

  const send = async () => {
    if (!input.trim() || pending) return;
    if (!status?.ok) return;
    const text = input.trim();
    setInput("");
    const userMsgId = `u-${Date.now()}`;
    setMessages((p) => [...p, { id: userMsgId, role: "user", content: text, ts: Date.now() }]);
    setPending(true);

    const assistantMsgId = `a-${Date.now()}`;
    let assistantText = "";
    const ensureAssistant = () => {
      setMessages((prev) => {
        if (prev.find((m) => m.id === assistantMsgId)) return prev;
        return [...prev, { id: assistantMsgId, role: "assistant", content: "", ts: Date.now() }];
      });
    };

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const token = getToken();
      const res = await fetch(`${getApiBase()}/chat/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, session_id: sessionId ?? undefined }),
        signal: ctrl.signal,
      });

      if (!res.ok || !res.body) {
        const errBody = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${errBody.slice(0, 200)}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const state = { buf: "" };

      // Read stream
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });

        parseSSE(chunk, (type, data) => {
          if (type === "session") {
            if ((data as { session_id?: string }).session_id) {
              setSessionId((data as { session_id: string }).session_id);
            }
          } else if (type === "text") {
            const t = String((data as { text?: string }).text ?? "");
            if (t) {
              assistantText += t;
              ensureAssistant();
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: assistantText } : m,
                ),
              );
            }
          } else if (type === "thinking") {
            // Could show thinking separately; for MVP skip
          } else if (type === "tool_use") {
            const toolName = String((data as { tool?: string }).tool ?? "?");
            setMessages((prev) => [
              ...prev,
              {
                id: `tool-${Date.now()}-${Math.random()}`,
                role: "tool",
                content: `🔧 ${toolName}`,
                toolInput: (data as { input?: unknown }).input,
                ts: Date.now(),
                tool: toolName,
              },
            ]);
          } else if (type === "tool_result") {
            // tool_result auto-follows tool_use; don't render separately for MVP
          } else if (type === "system") {
            // initial claude session init — note model
            const model = (data as { model?: string }).model;
            if (model) {
              setMessages((prev) => [
                ...prev,
                {
                  id: `sys-${Date.now()}`,
                  role: "system",
                  content: `model: ${model}`,
                  ts: Date.now(),
                },
              ]);
            }
          } else if (type === "result") {
            const t = String((data as { text?: string }).text ?? "");
            if (t && !assistantText) {
              assistantText = t;
              ensureAssistant();
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: t } : m,
                ),
              );
            }
          } else if (type === "error") {
            setMessages((prev) => [
              ...prev,
              {
                id: `err-${Date.now()}`,
                role: "tool",
                content: `Ошибка: ${String((data as { text?: string }).text ?? "")}`,
                ts: Date.now(),
              },
            ]);
          } else if (type === "end") {
            // handled below after loop
          }
        }, state);
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        setMessages((p) => [
          ...p,
          {
            id: `err-${Date.now()}`,
            role: "tool",
            content: `Сетевая ошибка: ${String(err)}`,
            ts: Date.now(),
          },
        ]);
      }
    } finally {
      setPending(false);
      abortRef.current = null;
    }
  };

  const stopGeneration = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setPending(false);
  };

  const clearChat = () => {
    if (pending) stopGeneration();
    setMessages([]);
    setSessionId(null);
  };

  return (
    <div className="flex h-full flex-col">
      <header className="mb-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Claude</h1>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button onClick={clearChat} className="btn text-xs">Очистить</button>
            )}
          </div>
        </div>

        {status?.ok && (
          <div className="mt-2 flex items-center gap-2 text-xs text-panel-muted">
            <span className="pill pill-ok">✅ Claude готов</span>
            <span>auth: {status.auth_method ?? "?"}</span>
            <button
              onClick={async () => {
                if (!confirm("Выйти? Токен будет удалён — нужно будет снова войти. Backend перезапустится.")) return;
                await api.chatLogout();
                setShowLogin(true);
                setTimeout(refreshStatus, 1500);
              }}
              className="btn text-xs ml-auto"
            >
              🚪 Выйти / сменить аккаунт
            </button>
          </div>
        )}

        {status && !status.ok && !showLogin && (
          <div className="card mt-2 border-panel-warn">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-sm font-medium text-panel-warn">Claude пока не готов</div>
                <div className="text-xs text-panel-muted mt-1">{status.reason}</div>
              </div>
              <button onClick={() => setShowLogin(true)} className="btn btn-primary whitespace-nowrap">
                Войти
              </button>
            </div>
          </div>
        )}

        {showLogin && (
          <div className="card mt-2 border-panel-accent space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Вход в Claude</div>
              <button onClick={() => setShowLogin(false)} className="btn text-xs">Закрыть</button>
            </div>
            <div className="text-xs text-panel-muted space-y-2">
              <div>Одна команда в твоём терминале:</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-panel-bg p-2 text-[11px] font-mono select-all break-all">
                  {sshCommand}
                </code>
                <button
                  onClick={() => copyToClipboard(sshCommand)}
                  className={
                    "btn text-xs whitespace-nowrap " +
                    (copyStatus === "copied" ? "border-panel-ok text-panel-ok" : "") +
                    (copyStatus === "failed" ? "border-panel-err text-panel-err" : "")
                  }
                >
                  {copyStatus === "copied" ? "✅ Скопировано" : copyStatus === "failed" ? "❌" : "📋 Копировать"}
                </button>
              </div>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Открой терминал на ноуте → вставь команду → Enter</li>
                <li>Открой ссылку в браузере, авторизуйся под нужным аккаунтом</li>
                <li>Скопируй длинный токен (начинается с <code>sk-ant-oat01-</code>) — тот что CLI напечатает в конце</li>
                <li>Вставь ниже:</li>
              </ol>
              <TokenPasteField onSaved={() => { setShowLogin(false); refreshStatus(); }} />
            </div>
            <div className="flex gap-2">
              <button onClick={refreshStatus} className="btn btn-primary">🔄 Проверить</button>
              <span className="text-xs text-panel-muted self-center">
                Сейчас: {status?.ok ? "✅ готов" : "❌ не готов"}
              </span>
            </div>
          </div>
        )}
      </header>

      <div
        ref={scrollerRef}
        className="flex-1 space-y-2 overflow-y-auto rounded border border-panel-border bg-panel-bg p-3"
      >
        {messages.length === 0 && (
          <div className="text-xs text-panel-muted">
            Начни разговор. Claude с твоей подпиской — доступен мульти-агент (Task, Agent, subagents), tools (Read/Write/Bash/Grep/…), 1M контекст на Opus 4.x.
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={
              "rounded p-2 text-sm " +
              (m.role === "user"
                ? "bg-panel-border"
                : m.role === "assistant"
                ? "bg-panel-card"
                : m.role === "tool"
                ? "bg-panel-accent/10 border border-panel-accent/30"
                : "bg-panel-bg border border-panel-border/50 text-panel-muted")
            }
          >
            <div className="mb-0.5 text-[10px] uppercase text-panel-muted">{m.role}</div>
            <div className="whitespace-pre-wrap break-words">{m.content}</div>
            {m.role === "tool" && m.toolInput != null && (
              <pre className="mt-1 overflow-x-auto rounded bg-panel-bg p-2 text-[10px] text-panel-muted">
                {JSON.stringify(m.toolInput, null, 2).slice(0, 600)}
              </pre>
            )}
          </div>
        ))}
        {pending && (
          <div className="text-xs text-panel-muted">
            <span className="animate-pulse">◦</span> Claude работает…{" "}
            <button onClick={stopGeneration} className="underline hover:text-panel-err">прервать</button>
          </div>
        )}
      </div>

      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={!status?.ok}
          placeholder={
            status?.ok
              ? pending
                ? "Claude думает — но ты можешь печатать следующий вопрос…"
                : "Спроси Claude…"
              : "Claude не готов — нажми «Войти»"
          }
          className="flex-1 rounded border border-panel-border bg-panel-bg px-3 py-2 text-sm"
        />
        <button className="btn btn-primary" disabled={!status?.ok || pending || !input.trim()}>
          {pending ? "…" : "Отправить"}
        </button>
      </form>
    </div>
  );
}

// Sub-component to paste token + save to server
function TokenPasteField({ onSaved }: { onSaved: () => void }) {
  const [tok, setTok] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!tok.startsWith("sk-ant-")) {
      setErr("Токен должен начинаться с sk-ant-");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const token = getToken();
      const res = await fetch(`${getApiBase()}/chat/login/save-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ token: tok.trim() }),
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${errBody.slice(0, 200)}`);
      }
      onSaved();
    } catch (e) {
      setErr(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={tok}
          onChange={(e) => setTok(e.target.value)}
          placeholder="sk-ant-oat01-..."
          className="flex-1 rounded border border-panel-border bg-panel-bg px-3 py-2 text-sm font-mono"
          autoFocus
        />
        <button onClick={save} disabled={saving || !tok.startsWith("sk-ant-")} className="btn btn-primary">
          {saving ? "Сохраняю…" : "Сохранить"}
        </button>
      </div>
      {err && <div className="text-xs text-panel-err">{err}</div>}
    </div>
  );
}
